import {
    IHomeItemDetailInput,
    IProductDetailModel,
    ISmallProductModel,
} from "@types";
import { convertStringNumber } from "@utils/digits";
import Link from "next/link";
import { useRouter } from "next/router";
import ImagePrepare from "../imagePrepare";
import useParentSize from "@hooks/useParentSize";
import { generateProductLink } from "./linkGenerator";
import ProductPrice from "./productPrice";
import WishlistIcon from "./wishlistIcon";
import { BsDash, BsPlus, BsTrash } from "react-icons/bs";
import { convertNumber } from "@utils/digits";
import { useAppContext } from "@context/app";
import { updateCart } from "@utils/helpers";
import { toast } from "react-toastify";
import { getNotifyConfig } from "@utils/helpers";
import { addToCart } from "@utils/addToCart";
import { segmentEvent } from "@utils/segment";

interface PersonalizationCartCardProps {
    product: IProductDetailModel;
    searchAction?: () => void;
    width?: number;
    source?: string;
    sourceEntity?: string;
    sourceSlug?: string;
    section?: string;
    sectionEntity?: string;
    sectionSlug?: string;
    q?: string;
    highImagePriority?: boolean;
    cartQuantity?: number;
    personalizationTitle?: string;
    personalizationType?: string;
    personalizationSource: string;
    personalizationItemIndex: number;
}

const PersonalizationCartCard: React.FC<PersonalizationCartCardProps> = ({
    product,
    searchAction,
    width,
    source,
    sourceEntity,
    sourceSlug,
    section,
    sectionEntity,
    sectionSlug,
    q,
    highImagePriority = false,
    cartQuantity = 0,
    personalizationTitle,
    personalizationType,
    personalizationSource,
    personalizationItemIndex,
}) => {
    const { locale } = useRouter();
    const [parentSize, parentRef] = useParentSize();
    const { setUserCart, user, country, area, eventProperties } = useAppContext();
    // const { skuAdded, skuRemoved } = useEvents();

    const sku = product.skus?.find((sku) => sku.id === product.default_sku_id);
    if (!sku) return null;

    const widthClass = width ? "w-[194px]" : `p-2 max-w-[194px] min-w-[180px] md:w-[194px] sm:w-[180px] xs:w-1/2 w-1/2`;
    const linkHref = generateProductLink(product, source, sourceSlug, sourceEntity, section, sectionEntity, sectionSlug, q);
    const badgeBackgroundColor = sku.price.badge?.background_color || "#FFD600";
    const badgeTextColor = sku.price.badge?.text_color;
    const badgeText = convertStringNumber(locale, sku.price.badge?.text);

    const handleAddToCart = () => {
        if (cartQuantity === 0) {
            // Add new item to cart
            setUserCart(addToCart(
                [],
                product,
                () => {
                    // Fire personalization item added to cart event
                    segmentEvent("Personalization Item Added To Cart", {
                        area_id: area?.id,
                        area_slug: area?.slug,
                        country_id: country?.id,
                        country_slug: country?.slug,
                        environment: process.env.NEXT_PUBLIC_SENTRY_ENV,
                        ...eventProperties,
                        language: locale?.includes("en") ? "English" : "Arabic",
                        user_id: user?.data?.id || -1,
                        personalization_title: personalizationTitle,
                        personalization_type: personalizationType,
                        product_id: product.id,
                        product_name: product.name,
                        personalization_item_index: personalizationItemIndex,
                        recommendations_source: personalizationSource,
                        sku_id: sku?.id,
                        price: sku?.price.price,
                        quantity: 1,
                    });
                },
                1
            ));

        } else {
            // Increase quantity
            handleIncreaseQty();
        }
    };

    const handleIncreaseQty = () => {
        if (cartQuantity < (sku?.buy_limit || 999)) {
            setUserCart(addToCart(
                [],
                product,
                () => {
                    // skuAdded(source || "personalization", sku);
                },
                1
            ));
        } else {
            toast.error(
                `Only ${sku?.buy_limit} items remaining`,
                getNotifyConfig()
            );
        }
    };

    const handleDecreaseQty = () => {
        const data: any[] = updateCart();
        const itemIndex = data.findIndex(item => item.id === sku?.id);
        if (itemIndex !== -1) {
            if (data[itemIndex].qty > 1) {
                data[itemIndex].qty -= 1;
                setUserCart([...data]);
                // skuRemoved(source || "personalization", sku, "Remove");
            } else {
                // Remove item completely
                setUserCart(data.filter(item => item.id !== sku?.id));
                // skuRemoved(source || "personalization", sku, "Delete");
            }
        }
    };

    return (
        <div className={widthClass}>
            <div className="absolute top-3 right-3 z-10">
                <WishlistIcon productId={product.id} />
            </div>
            <Link
                href={linkHref}
                prefetch={false}
                onClick={(e) => {
                    // Fire personalization item clicked event
                    segmentEvent("Personalization Item Clicked", {
                        area_id: area?.id,
                        area_slug: area?.slug,
                        country_id: country?.id,
                        country_slug: country?.slug,
                        environment: process.env.NEXT_PUBLIC_SENTRY_ENV,
                        ...eventProperties,
                        language: locale?.includes("en") ? "English" : "Arabic",
                        user_id: user?.data?.id || -1,
                        personalization_title: personalizationTitle,
                        personalization_type: personalizationType,
                        personalization_item_index: personalizationItemIndex,
                        recommendations_source: personalizationSource,
                        product_id: product.id,
                        product_name: product.name,
                        sku_id: sku?.id,
                    });
                    searchAction?.();
                }}
            >
                <div className={`relative p-3 border rounded-xl text-[#333] ${width ? "h-[296px]" : "sm:h-[296px] xs:h-[280px] h-[280px]"}  flex flex-col`}>
                    <div className="w-full max-w-[180px] p-0 m-auto">
                        <div className="w-full pt-[100%] relative overflow-hidden">
                            <div className="w-full h-full absolute top-0 left-0 rounded-lg overflow-hidden" ref={parentRef}>
                                {sku.medias && <ImagePrepare alt={product.name} media={sku.medias[0]} width={parentSize.width} priority={highImagePriority} fetchPriority={highImagePriority ? "high" : "auto"} />}
                            </div>

                            {/* Cart Controls Overlay */}
                            <div className="">
                                {cartQuantity === 0  ? (
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleAddToCart();
                                        }}
                                        disabled={sku.buy_limit <= 0}
                                        className={`absolute bottom-2 right-2 z-20 w-8 h-8 rounded-full border-2 border-[#008AF6] text-white bg-button-primary hover:scale-110 transition-all duration-300 flex items-center justify-center shadow-md ${sku.buy_limit <= 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                                    >
                                        <BsPlus size={20} />
                                    </button>
                                ) : (
                                    <div className="absolute bottom-2 w-full">

                                        <div
                                            className="w-full z-20 flex items-center justify-between gap-1 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 shadow-md"
                                            style={{ direction: "ltr" }}
                                        >
                                            <button
                                                className="w-6 h-6 rounded-full border-[#008AF6] text-[#008AF6] hover:scale-90 transition-all duration-300 flex items-center justify-center"
                                                style={{ borderWidth: cartQuantity > 1 ? "2px" : "0" }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleDecreaseQty();
                                                }}
                                            >
                                                {cartQuantity > 1 ? (
                                                    <BsDash size={16} />
                                                ) : (
                                                    <BsTrash className="cursor-pointer" size={16} />
                                                )}
                                            </button>
                                            <p className="text-[#333] text-center flex justify-center items-center font-extrabold text-sm mx-1 min-w-[20px]">
                                                {convertNumber(locale, cartQuantity)}
                                            </p>
                                            <button
                                                className="w-6 h-6 rounded-full border-[#008AF6] border-2 text-[#008AF6] hover:scale-105 transition-all duration-300 flex items-center justify-center"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleIncreaseQty();
                                                }}
                                            >
                                                <BsPlus size={16} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="w-full mt-5 bg-white flex flex-col justify-between flex-1">
                        <h2 className="font-normal text-sm line-clamp-2 whitespace-normal">{convertStringNumber(locale, product.name)}</h2>
                        <div className="flex justify-between items-end">
                            <ProductPrice
                                oldPrice={sku.price.old_price}
                                price={sku.price.price}
                                buyLimit={sku.buy_limit}
                            />
                            {(sku.buy_limit && sku.price.old_price) ? (
                                <div className="w-7 h-7 rounded-lg flex justify-center items-center" style={{ backgroundColor: badgeBackgroundColor, color: badgeTextColor }}>
                                    <p className="font-bold text-[10px]">{badgeText}</p>
                                </div>) : null}
                        </div>
                    </div>
                </div>
            </Link>
        </div>
    );
};

export default PersonalizationCartCard;
